import { Alert, Button, Card, CardContent, CardFooter, CardHeader, CardTitle, Datepicker, Input } from '@ibsys/kit';
import { FC, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useAttribute } from '@/entities/attributes';
import {
  C1ToAsrCallsParams,
  useC1ToAsrCalls,
  useIntegration,
  useStartC1ToAsrTranscription,
} from '@/entities/integrations';
import { useHasPermission } from '@/entities/profile';
import { toNumberOrUndefined } from '@/shared/lib/utils/number';
import { toastError, toastSuccess } from '@/shared/lib/utils/toast';

import { C1ToAsrCallsResult } from './C1ToAsrCallsResult';
import { C1ToAsrSendSummary, C1ToAsrSendSummaryData } from './C1ToAsrSendSummary';

// Атрибут, хранящий линию обслуживания звонка: без него отбор невозможен
const LINE_ATTRIBUTE_CODE = 'line';
// Интеграция, из которой можно получить аудиозапись звонка перед отправкой
const C1_CRM_CODE = 'c1_crm';
// Максимум звонков в отборе по умолчанию; пользователь может задать больше или меньше
const DEFAULT_CALLS_LIMIT = 500;
// Меньше одного звонка отбирать нечего
const MIN_CALLS_LIMIT = 1;
// 404 на запрос атрибута означает «не заведён», а не сбой чтения
const NOT_FOUND_STATUS = 404;

// Интервал дат звонка; обе границы календарь отдаёт как `yyyy-MM-dd`
type C1ToAsrDateRange = {
  from?: string;
  to?: string;
};

// Условия отбора звонков
type C1ToAsrFiltersForm = {
  dateRange?: C1ToAsrDateRange;
  line?: string;
  limit?: number;
};

/**
 * Блок карточки интеграции c1_to_asr: отбор звонков по интервалу дат и линии,
 * выбор найденных звонков и отправка их аудио на распознавание.
 */
export const C1ToAsrSection: FC = () => {
  const { t } = useTranslation(['integrations', 'common']);
  const canAdmin = useHasPermission('ADMIN_INTEGRATION_INFO');

  // Активные условия — то, что реально отправлено в запрос отбора
  const [activeParams, setActiveParams] = useState<C1ToAsrCallsParams | undefined>(undefined);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [summary, setSummary] = useState<C1ToAsrSendSummaryData | undefined>(undefined);
  // Режим обработки распознанной речи: относится ко всей отправке, а не к отбору,
  // поэтому живёт вне формы фильтров и переживает их сброс.
  // По умолчанию обогащение выключено: массовая догрузка сотен звонков стоит дорого,
  // поэтому обогащение включает сам пользователь
  const [isEnrichEnabled, setIsEnrichEnabled] = useState(false);

  const { control, handleSubmit, reset, watch } = useForm<C1ToAsrFiltersForm>({
    defaultValues: { dateRange: undefined, line: '', limit: DEFAULT_CALLS_LIMIT },
  });

  // Линия запрашивается точечно по коду; эндпоинт по умолчанию ищет среди простых атрибутов
  const { data: lineAttribute, error: lineAttributeError } = useAttribute(LINE_ATTRIBUTE_CODE, undefined, {
    silentError: true,
  });

  const hasLineAttribute = Boolean(lineAttribute);
  const isLineAttributeMissing = lineAttributeError?.status === NOT_FOUND_STATUS;
  // Запрос не дошёл до ответа: наличие линии неизвестно — это не то же самое, что «атрибут не заведён»
  const isLineAttributeUnknown = Boolean(lineAttributeError) && !isLineAttributeMissing;

  // Незаведённая или недоступная C1 CRM равносильна выключенной: аудио получить неоткуда
  const { data: c1CrmIntegration } = useIntegration(C1_CRM_CODE);
  const isC1CrmActive = Boolean(c1CrmIntegration?.isActive);

  const { data, isLoading, isFetching, isError } = useC1ToAsrCalls(activeParams);
  const { mutateAsync: startTranscription, isPending: isSending } = useStartC1ToAsrTranscription();

  // По умолчанию отмечены все звонки выдачи; при усечении это не все найденные — о чём говорит результат отбора
  useEffect(() => {
    setSelectedIds(data?.calls.map((call) => call.id) ?? []);
  }, [data]);

  const handleSearch = useCallback((values: C1ToAsrFiltersForm) => {
    const dateFrom = values.dateRange?.from;
    const dateTo = values.dateRange?.to;
    const line = values.line?.trim();
    const limit = toNumberOrUndefined(values.limit);
    if (!dateFrom || !dateTo || !line || limit === undefined) return;

    setSummary(undefined);
    setActiveParams({ dateFrom, dateTo, line, limit });
  }, []);

  const handleReset = useCallback(() => {
    reset({ dateRange: undefined, line: '', limit: DEFAULT_CALLS_LIMIT });
    setActiveParams(undefined);
    setSelectedIds([]);
    setSummary(undefined);
  }, [reset]);

  const handleSend = useCallback(async () => {
    if (!activeParams) return;
    try {
      const response = await startTranscription({ conversationIds: selectedIds, enrich: isEnrichEnabled });
      setSummary({
        dateFrom: activeParams.dateFrom,
        dateTo: activeParams.dateTo,
        line: activeParams.line,
        limit: activeParams.limit,
        isEnrichEnabled,
        conversationIds: response.conversationIds,
      });
      reset({ dateRange: undefined, line: '', limit: DEFAULT_CALLS_LIMIT });
      setActiveParams(undefined);
      setSelectedIds([]);
      toastSuccess(t('integrations:c1ToAsr.sendSuccess'));
    } catch {
      toastError(t('integrations:c1ToAsr.sendError'));
    }
  }, [activeParams, isEnrichEnabled, reset, selectedIds, startTranscription, t]);

  // Отбор запускается только при заведённой линии и полностью заданных условиях.
  // Границы максимума проверяет валидация поля: недопустимое значение должно
  // показать причину отказа, а не молча заблокировать кнопку
  const formValues = watch();
  const canSearch =
    hasLineAttribute &&
    Boolean(formValues.dateRange?.from) &&
    Boolean(formValues.dateRange?.to) &&
    Boolean(formValues.line?.trim()) &&
    toNumberOrUndefined(formValues.limit) !== undefined;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-xl">{t('integrations:c1ToAsr.title')}</CardTitle>
      </CardHeader>
      <form onSubmit={handleSubmit(handleSearch)}>
        <CardContent className="flex flex-col gap-3">
          <p className="text-muted-foreground text-sm">{t('integrations:c1ToAsr.description')}</p>

          {isC1CrmActive && (
            <Alert
              type="info"
              title={t('integrations:c1ToAsr.c1CrmActive.title')}
              description={t('integrations:c1ToAsr.c1CrmActive.description')}
            />
          )}

          {isLineAttributeUnknown && <Alert type="error" title={t('integrations:c1ToAsr.lineAttributeCheckError')} />}

          {isLineAttributeMissing && <Alert type="error" title={t('integrations:c1ToAsr.missingLineAttribute')} />}

          {!isLineAttributeMissing && !isLineAttributeUnknown && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <Datepicker
                mode="range"
                outputType="iso-date"
                control={control}
                name="dateRange"
                label={t('integrations:c1ToAsr.filter.dateRange')}
              />
              <Input
                control={control}
                name="line"
                label={t('integrations:c1ToAsr.filter.line')}
                placeholder={t('integrations:c1ToAsr.filter.linePlaceholder')}
              />
              <Input
                control={control}
                name="limit"
                type="number"
                label={t('integrations:c1ToAsr.filter.limit')}
                description={t('integrations:c1ToAsr.filter.limitDescription')}
                rules={{
                  required: { value: true, message: t('common:required') },
                  min: { value: MIN_CALLS_LIMIT, message: t('integrations:c1ToAsr.filter.limitMin') },
                }}
              />
            </div>
          )}
        </CardContent>
        <CardFooter className="flex flex-wrap gap-3">
          <Button type="submit" disabled={!canSearch}>
            {t('integrations:c1ToAsr.filter.search')}
          </Button>
          <Button type="button" variant="secondary" onClick={handleReset}>
            {t('integrations:c1ToAsr.filter.clear')}
          </Button>
        </CardFooter>
      </form>

      {activeParams && (
        <CardContent>
          <C1ToAsrCallsResult
            calls={data?.calls ?? []}
            total={data?.total ?? 0}
            selectedIds={selectedIds}
            isLoading={isLoading || isFetching}
            isError={isError}
            canSend={canAdmin}
            isSending={isSending}
            isEnrichEnabled={isEnrichEnabled}
            onSelectionChange={setSelectedIds}
            onEnrichChange={setIsEnrichEnabled}
            onSend={handleSend}
          />
        </CardContent>
      )}

      {summary && (
        <CardContent>
          <C1ToAsrSendSummary summary={summary} />
        </CardContent>
      )}
    </Card>
  );
};
